/**
 * [ADR-0004] Image Library — interfaces cho feature thư viện ảnh/file.
 * Port từ FE13 ImageLibComponent, dùng trong ImageLibraryDialogComponent.
 */

/** Một item trong thư viện ảnh/file (từ API /business/attachment/recentlist). */
export interface IImageLibraryItem {
  id: string;
  url: string;
  uri?: string;
  title: string;
  isFavorite: number;
  createdTime: number;
  hashFile?: string;
  /** Token file đã upload lên Facebook & Zalo OA; dùng lại khi gửi file để tránh upload thừa. */
  faceAttachmentId?: string;
  businessId?: string;
  fanpageId?: string;
  categoryId?: string;
  status?: number;
  /** Loại file xác định bởi context mở dialog (image/file/video/audio) */
  fileType?: ImageLibraryFileType;
  /** Thumbnail URL (FE13 trả thumbUrl riêng cho video/large images) */
  thumbUrl?: string;
  /** Shopee video ID — FE13 file.ecomVideoId, cần cho Shopee video send */
  ecomVideoId?: string;
  /** Ảnh đến từ sản phẩm Nhanh POS, không phải attachment thư viện. */
  isProductImage?: boolean;
  originProductId?: string;
}

/** Loại file mà dialog đang hiển thị. */
export type ImageLibraryFileType = 'image' | 'file' | 'video' | 'audio';

/** Thứ tự gửi khi ảnh được gửi kèm một tin nhắn văn bản. */
export type ImageLibrarySendPriority = '1' | '2';

/** Scope lưu attachment, đồng bộ Business\\Model\\Attachment trên backend. */
export const ATTACHMENT_UPLOAD_SCOPE = {
  BUSINESS: 1,
  PAGE: 2,
} as const;

export type AttachmentUploadScope = typeof ATTACHMENT_UPLOAD_SCOPE[keyof typeof ATTACHMENT_UPLOAD_SCOPE];

/** Params mở ImageLibraryDialogComponent qua NgbModal. */
export interface IImageLibraryDialogData {
  /** Loại file muốn browse/upload */
  type: ImageLibraryFileType;
  /** ID page hiện tại (nếu có) */
  pageId?: string;
  /** ID business hiện tại (nếu có) */
  businessId?: number;
  /** Số item tối đa được chọn (default: 50) */
  limit?: number;
  /** Text nút submit (default: "Gửi ảnh" / "Gửi file") */
  okBtnText?: string;
  /** Context mở dialog — điều khiển tab visibility */
  openFrom?: string;
  /** Channel type enum — filter extension theo kênh */
  channel?: number;
  /** Cho phép nhập nội dung gửi kèm ảnh trong context hỗ trợ composed message. */
  allowMessageComposer?: boolean;
}

/** Kết quả trả về từ ImageLibraryDialogComponent. */
export interface IImageLibraryResult {
  /** Items từ thư viện (có URL sẵn, không cần upload) */
  libraryItems?: IImageLibraryItem[];
  /** Files mới upload (cần upload lên cloud) */
  newFiles?: File[];
  /** Nội dung gửi kèm ảnh; chỉ có khi người dùng bật ô nhập và nhập nội dung hợp lệ. */
  message?: string;
  /** '1' = gửi ảnh trước, '2' = gửi chữ trước. */
  sendPriority?: ImageLibrarySendPriority;
}

/** Params cho API /business/attachment/recentlist. */
export interface IRecentListParams {
  businessId?: number;
  pageId?: string;
  limit?: number;
  after?: any[];
  categoryId?: string;
  typeUse?: number;
  isFavorite?: string | number;
  isFile?: string;
  extension?: string;
  search?: string;
}

/** Response shape từ /business/attachment/recentlist. */
export interface IRecentListResponse {
  scroller?: {
    items: IImageLibraryItem[];
    next?: any[] | false;
  };
}

/** Loại thao tác được lưu trong nhật ký attachment. */
export const ATTACHMENT_LOG_TYPE = {
  DELETE: 1,
  RENAME: 2,
  RESTORE: 3,
} as const;

export type AttachmentLogType = typeof ATTACHMENT_LOG_TYPE[keyof typeof ATTACHMENT_LOG_TYPE];

export interface IAttachmentLogSnapshot {
  title?: string;
  url?: string;
  [key: string]: unknown;
}

export interface IAttachmentLogItem {
  id: string;
  attachmentId: string;
  type: AttachmentLogType;
  createdById?: string;
  createdByName?: string;
  createdAt: number;
  expiresAt: number;
  objectTitle?: string;
  objectUri?: string;
  dataBefore?: IAttachmentLogSnapshot | null;
  dataAfter?: IAttachmentLogSnapshot | null;
  relatedLogId?: string | null;
  canRestore: boolean;
}

export interface IAttachmentLogListParams {
  businessId?: number;
  pageId?: string;
  limit?: number;
  after?: any[];
}

export interface IAttachmentLogListResponse {
  result: IAttachmentLogItem[];
  next?: any[] | false | null;
  totalItems?: number;
}

export interface IAttachmentRestoreResponse {
  restoredAttachmentIds?: string[];
}

/** Response shape từ /business/attachment/listcategory. */
export interface ICategoryListResponse {
  scroller?: {
    items: ICategoryItem[];
    next?: any;
    categoryImage?: ICategoryTreeNode[];
    categoryFile?: ICategoryTreeNode[];
  };
}

/** Category item thô từ API. */
export interface ICategoryItem {
  id: string;
  name: string;
  parentId?: string;
  createdTime?: number;
}

/** Category node đã build tree. */
export interface ICategoryTreeNode {
  id: string;
  name: string;
  parentId?: string;
  /** Level trong category tree: 1 = root, 2 = child, 3 = grandchild */
  level?: number;
  children?: ICategoryTreeNode[];
}

/** Accept filter values cho từng loại file. */
export const IMAGE_LIBRARY_ACCEPT: Record<ImageLibraryFileType, string> = {
  image: '.gif,.jpg,.png,.heic,.jpeg,.tiff,.bmp',
  file: '.xml,.cdr,.mov,.mkv,.avi,.rmvb,.mp4,.m4v,.mpg,.mpeg,.3gp,.acc,.flac,.m4a,.m4p,.mp3,.wav,.wma,.doc,.docx,.pdf,.xlsx,.xlsm,.csv,.zip,.rar,.7z,.gzip,.tar',
  video: '.mkv,.avi,.rmvb,.mp4,.m4v,.mpg,.mpeg,.3gp,.mov',
  audio: '.acc,.flac,.m4a,.m4p,.mp3,.wav,.wma',
};

/** Max file size theo loại (bytes). */
export const IMAGE_LIBRARY_MAX_SIZE: Record<ImageLibraryFileType, number> = {
  image: 8 * 1024 * 1024, // 8MB
  file: 25 * 1024 * 1024, // 25MB
  video: 25 * 1024 * 1024, // 25MB
  audio: 25 * 1024 * 1024, // 25MB
};
